import { type SelectedProduct, useTradeSalesStore } from "@fero/stores";
import { LoadingButton } from "@mui/lab";
import { Box, Stack } from "@mui/material";
import type { BigDecimal } from "adl-gen/common";
import type { ProductId } from "adl-gen/ferovinum/app/db";
import { BaseWizardCard } from "components/library/widgets/wizard-card/base-wizard-card";
import type { FormikProps } from "formik";
import { useMemo } from "react";
import { useManageBrowseProducts } from "../hooks";
import type { SelectedProductsFormValues } from "../new-organisation-purchase-request-setup-page";
import { BrowseProductsModal } from "./browse-products-modal";
import { NewProductSelectionTable } from "./new-product-selection-table";
import { ProductSearchBar } from "./product-search-bar";

type Props = {
  form: FormikProps<SelectedProductsFormValues>;
  savedPurchaserDiscountPercentage?: BigDecimal;
  producibles: Map<ProductId, string>;
  isProducibleDataLoading: boolean;
};

export const NewProductSelectionCard = ({
  form,
  savedPurchaserDiscountPercentage,
  producibles,
  isProducibleDataLoading,
}: Props) => {
  const { options } = useTradeSalesStore();
  const manageBrowseProducts = useManageBrowseProducts();

  // TODO can probably remove this in favour of in the search bar component
  const selectedProductCodes = useMemo(
    () =>
      new Set(form.values.selectedProducts.map((p) => p.summary.value.code)),
    [form.values.selectedProducts],
  );

  const applyDiscountIfNecessary = (product: SelectedProduct) => {
    if (savedPurchaserDiscountPercentage) {
      return {
        ...product,
        lineItemDiscount: Number(savedPurchaserDiscountPercentage),
      };
    }
    return product;
  };

  return (
    <Stack spacing={5}>
      <BrowseProductsModal
        onAddProducts={(products) => {
          const selectedProducts = form.values.selectedProducts;
          const purchaserDiscount = savedPurchaserDiscountPercentage;
          const newSelectedProducts = products.filter(
            (product) =>
              !selectedProducts.some(
                (selectedProduct) =>
                  selectedProduct.summary.value.code ===
                  product.summary.value.code,
              ),
          );

          let newSelectedProductsWithDiscounts = newSelectedProducts;
          if (purchaserDiscount) {
            newSelectedProductsWithDiscounts = newSelectedProducts.map(
              applyDiscountIfNecessary,
            );
          }
          form.setFieldValue("selectedProducts", [
            ...selectedProducts,
            ...newSelectedProductsWithDiscounts,
          ]);
        }}
        manageBrowseProducts={manageBrowseProducts}
        producibles={producibles}
      />
      <BaseWizardCard
        title={"Stock Selection"}
        subtitle={
          options.storageLocation
            ? "Please select the stock you would like to include in this sale."
            : "Please select a Storage Location"
        }
        inputElement={
          <Stack spacing={2} flex={1}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              gap={2}
              sx={{ width: "50%" }}
            >
              <Box sx={{ width: "75%" }}>
                <ProductSearchBar
                  onSelectProduct={(p) =>
                    form.setFieldValue("selectedProducts", [
                      ...form.values.selectedProducts,
                      applyDiscountIfNecessary(p),
                    ])
                  }
                  selectedProductCodes={selectedProductCodes}
                  producibles={producibles}
                />
              </Box>
              <Box sx={{ width: "25%" }}>
                <LoadingButton
                  loading={isProducibleDataLoading}
                  variant="outlined"
                  fullWidth
                  onClick={() =>
                    manageBrowseProducts.openBrowse(
                      form.values.selectedProducts,
                    )
                  }
                >
                  BROWSE
                </LoadingButton>
              </Box>
            </Stack>
            <NewProductSelectionTable form={form} />
          </Stack>
        }
      />
    </Stack>
  );
};
